import React from 'react';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import { AutocompleteRenderOptionState } from '@material-ui/lab/Autocomplete';
import { useApi } from '@backstage/core-plugin-api';
import {
  DefaultEntityFilters,
  EntityAutocompletePicker,
  EntityFilter,
  catalogApiRef,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';

/** The field the catalog indexes the platform team of a product under. */
export const PLATFORM_TEAM_FIELD = 'spec.platformTeam';

/**
 * Narrows the catalog to the products a platform team runs.
 *
 * The field holds the vuid of the team, which is what the team entity is named
 * after, so the value of the filter is both what the picker shows and what the
 * catalog is asked for - no lookup in between.
 *
 * @public
 */
export class EntityPlatformTeamFilter implements EntityFilter {
  constructor(readonly values: string[]) {}

  getCatalogFilters(): Record<string, string | string[]> {
    return { [PLATFORM_TEAM_FIELD]: this.values };
  }

  toQueryValue(): string[] {
    return this.values;
  }
}

/**
 * The filters of the catalog page of this app: the stock ones, plus the
 * platform team.
 *
 * @public
 */
export type RtlEntityFilters = DefaultEntityFilters & {
  platformTeams?: EntityPlatformTeamFilter;
};

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

/**
 * Picks the team that operates the infrastructure, to answer "what does my
 * team run" for a whole portfolio at once - the product page answers it one
 * product at a time.
 *
 * Only products carry the field, and the picker takes its options from a facet
 * scoped to the kind that is being listed, so it leaves itself out of the
 * sidebar for every other kind rather than offering an empty dropdown.
 *
 * @public
 */
export const PlatformTeamPicker = () => {
  const catalogApi = useApi(catalogApiRef);
  const { filters } = useEntityList<RtlEntityFilters>();

  // The picker renders nothing for a kind that carries no platform team, and
  // the catalog page starts out on Components - so the names below would be
  // fetched on every visit to the busiest page of the app and never read. A
  // listing of no particular kind can still turn up products, hence the
  // undefined.
  const kind = filters.kind?.value?.toLocaleLowerCase('en-US');
  const listsProducts = kind === undefined || kind === 'product';

  // The facet the options come from carries the vuid and nothing else, so the
  // readable name is fetched next to it - names and titles of the teams alone,
  // which is every value the facet can ever hold.
  const { value: titles } = useAsync(async () => {
    if (!listsProducts) {
      return {};
    }
    const { items } = await catalogApi.getEntities({
      filter: { kind: 'Group', 'spec.type': 'team' },
      fields: ['metadata.name', 'metadata.title'],
    });
    return Object.fromEntries(
      items.map(team => [team.metadata.name, team.metadata.title]),
    ) as Record<string, string | undefined>;
  }, [catalogApi, listsProducts]);

  // Both, because either one alone leaves the reader looking the other up: a
  // ticket or a CMDB query names the team by its vuid, a person names it by
  // its title. Same format as an entity link elsewhere in the app.
  const optionLabel = (vuid: string) => {
    const title = titles?.[vuid];
    return title && title !== vuid ? `${title} (${vuid})` : vuid;
  };

  return (
    <EntityAutocompletePicker<RtlEntityFilters, 'platformTeams'>
      label="Platform Team"
      name="platformTeams"
      path={PLATFORM_TEAM_FIELD}
      Filter={EntityPlatformTeamFilter}
      // The stock option renderer prints the raw facet value and ignores
      // `getOptionLabel`, so the dropdown needs its own row to show the name.
      getOptionLabel={optionLabel}
      renderOption={(
        option: string,
        { selected }: AutocompleteRenderOptionState,
      ) => (
        <FormControlLabel
          control={
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              checked={selected}
            />
          }
          label={optionLabel(option)}
          onClick={event => event.preventDefault()}
        />
      )}
    />
  );
};
